import { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "../components/Icon";
import { useTranslation } from "react-i18next";
import { appendDictation, Dictate } from "../components/Dictate";
import { ErrorText } from "../components/ErrorText";
import { Help } from "../components/Help";
import { useApp } from "../context";
import type { FeedbackKind, FeedbackReport } from "../data";
import { shrinkPhoto } from "../photo";
import { FEEDBACK_KINDS, FEEDBACK_NOT_OK_TEXT, FEEDBACK_TEXT, FEEDBACK_TITLE } from "../../../../supabase/functions/_shared/feedback";

import { APP_VERSION } from "../version";
export { APP_VERSION };

const blobBase64 = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });

/**
 * Hibajelzés: a report to the developer (bug, idea or question) on top, the workshop's reports with the
 * conversation below. When the developer asks for a check, the reporter answers "Ellenőriztem, jó" or
 * "Még hibás" (with a short reason). Phone first: big buttons, the form in one column.
 */
export function Feedback({ from }: { from: string }) {
  const { t, i18n } = useTranslation();
  const { store, workshop } = useApp();
  const [kind, setKind] = useState<FeedbackKind | null>(null);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [shot, setShot] = useState<{ data: string; url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [reports, setReports] = useState<FeedbackReport[] | null>(null);
  const [listError, setListError] = useState<unknown>(null);
  const camera = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setListError(null);
    try {
      setReports(await store.feedbackList(workshop.id));
    } catch (e) {
      setListError(e);
      setReports([]);
    }
  }, [store, workshop.id]);
  useEffect(() => void load(), [load]);

  const missing = [
    ...(kind ? [] : [t("feedback.kind")]),
    ...(title.trim().length >= FEEDBACK_TITLE.min ? [] : [t("feedback.subject")]),
    ...(text.trim().length >= FEEDBACK_TEXT.min ? [] : [t("feedback.text")]),
  ];
  const tooLong = title.trim().length > FEEDBACK_TITLE.max || text.trim().length > FEEDBACK_TEXT.max;

  const pickShot = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setError(null);
    try {
      const small = await shrinkPhoto(file);
      setShot({ data: await blobBase64(small.blob), url: typeof URL.createObjectURL === "function" ? URL.createObjectURL(small.blob) : "" });
    } catch (e) {
      setError(e);
    }
  };

  const send = async () => {
    if (!kind || missing.length || tooLong) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const r = await store.feedbackReport(workshop.id, {
        kind, title: title.trim(), text: text.trim(), page: from, appVersion: APP_VERSION, userAgent: globalThis.navigator?.userAgent ?? "", screenshot: shot?.data ?? null,
      });
      setInfo(t("feedback.sent", { number: String(r.issueNumber) }));
      setKind(null);
      setTitle("");
      setText("");
      setShot(null);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="feedback-page">
      <h1>
        {t("feedback.title")} <Help id="feedback.page" />
      </h1>
      <p className="muted">{t("feedback.intro")}</p>

      <div className="feedback-form">
        <fieldset className="choice-cards">
          <legend>{t("feedback.kind")}</legend>
          {FEEDBACK_KINDS.map((k) => (
            <label key={k} className={`choice-card${kind === k ? " selected" : ""}`}>
              <input type="radio" name="feedbackKind" checked={kind === k} onChange={() => setKind(k)} />
              <span>
                <strong>{t(`feedback.kind.${k}`)}</strong>
                <br />
                <span className="muted small">{t(`feedback.kindHint.${k}`)}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <label className="field">
          <span>{t("feedback.subject")}</span>
          <input value={title} maxLength={FEEDBACK_TITLE.max} onChange={(e) => setTitle(e.target.value)} aria-label={t("feedback.subject")} aria-invalid={title.trim().length < FEEDBACK_TITLE.min || undefined} />
        </label>
        <label className="field">
          <span>{t("feedback.text")}</span>
          <textarea rows={5} value={text} maxLength={FEEDBACK_TEXT.max} onChange={(e) => setText(e.target.value)} aria-label={t("feedback.text")} aria-invalid={text.trim().length < FEEDBACK_TEXT.min || undefined} />
        </label>
        <Dictate label={t("feedback.text")} onText={(s) => setText((v) => appendDictation(v, s))} />

        <div className="feedback-shot">
          <p className="feedback-shot-label">{t("feedback.screenshot")}</p>
          {shot ? (
            <div className="feedback-shot-preview">
              {shot.url && <img src={shot.url} alt={t("feedback.screenshotAdded")} />}
              <span>{t("feedback.screenshotAdded")}</span>
              <button type="button" onClick={() => setShot(null)}>
                {t("feedback.screenshotRemove")}
              </button>
            </div>
          ) : (
            <div className="photo-buttons">
              <button type="button" className="camera" onClick={() => camera.current?.click()}>
                <Icon name="photo" size={28} />
                {t("photos.take")}
              </button>
              <button type="button" onClick={() => gallery.current?.click()}>
                {t("feedback.screenshotPick")}
              </button>
            </div>
          )}
          <input ref={camera} type="file" accept="image/*" capture="environment" hidden aria-label={t("photos.take")} onChange={(e) => { void pickShot(e.target.files); e.target.value = ""; }} />
          <input ref={gallery} type="file" accept="image/*" hidden aria-label={t("feedback.screenshotPick")} onChange={(e) => { void pickShot(e.target.files); e.target.value = ""; }} />
        </div>

        <p className="muted small feedback-auto">{t("feedback.auto", { page: from, version: APP_VERSION })}</p>
        <div className="actions">
          <button type="button" className="primary" disabled={busy || missing.length > 0 || tooLong} onClick={() => void send()}>
            {busy ? t("feedback.sending") : t("feedback.send")}
          </button>
          {missing.length > 0 && <span className="missing">{t("required.stillMissing", { fields: missing.join(", ") })}</span>}
        </div>
        <ErrorText error={error} />
        {info && <p className="ok" role="status">{info}</p>}
      </div>

      <div className="feedback-head">
        <h2>{t("feedback.mine")}</h2>
        <button type="button" onClick={() => void load()}>
          {t("feedback.refresh")}
        </button>
      </div>
      <ErrorText error={listError} />
      {reports === null ? (
        <p>{t("common.loading")}</p>
      ) : reports.length === 0 ? (
        !listError && <p className="muted">{t("feedback.none")}</p>
      ) : (
        <ul className="feedback-list">
          {reports.map((r) => (
            <ReportCard key={r.issueNumber} report={r} lang={i18n.language} onChanged={load} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ReportCard({ report, lang, onChanged }: { report: FeedbackReport; lang: string; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const [notOk, setNotOk] = useState(false);
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const when = (iso: string) => new Date(iso).toLocaleString(lang, { dateStyle: "short", timeStyle: "short" });
  const open = report.status !== "done";
  const reasonShort = reason.trim().length < FEEDBACK_NOT_OK_TEXT.min;

  const answer = async (verdict: "ok" | "notOk" | "comment", body: string) => {
    setBusy(true);
    setError(null);
    try {
      await store.feedbackReply(workshop.id, report.issueNumber, verdict, body.trim());
      setNotOk(false);
      setReason("");
      setComment("");
      await onChanged();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className={`feedback-card status-${report.status}`}>
      <div className="feedback-card-head">
        <strong className="feedback-card-title">{report.title}</strong>
        <span className={`fb-chip fb-${report.status}`}>{t(`feedback.status.${report.status}`)}</span>
      </div>
      <p className="muted small">
        {t(`feedback.kind.${report.kind}`)} · #{report.issueNumber} · {when(report.createdAt)}
      </p>
      {report.text && <p className="feedback-text">{report.text}</p>}
      {report.comments.length > 0 && (
        <ol className="feedback-thread">
          {report.comments.map((c, i) => (
            <li key={i} className={c.fromDeveloper ? "from-dev" : "from-me"}>
              <p className="small feedback-who">
                <strong>{c.fromDeveloper ? t("feedback.developer") : t("feedback.reporter")}</strong> · {when(c.createdAt)}
              </p>
              <p className="feedback-text">{c.body}</p>
            </li>
          ))}
        </ol>
      )}

      {report.status === "check" && (
        <div className="feedback-check">
          <p className="feedback-check-ask">{t("feedback.checkAsk")}</p>
          {!notOk ? (
            <div className="actions">
              <button type="button" className="primary" disabled={busy} onClick={() => void answer("ok", "")}>
                {t("feedback.ok")}
              </button>
              <button type="button" className="warn-button" disabled={busy} onClick={() => setNotOk(true)}>
                {t("feedback.notOk")}
              </button>
            </div>
          ) : (
            <>
              <label className="field">
                <span>{t("feedback.notOkText")}</span>
                <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} aria-label={t("feedback.notOkText")} aria-invalid={reasonShort || undefined} />
              </label>
              <Dictate label={t("feedback.notOkText")} onText={(s) => setReason((v) => appendDictation(v, s))} />
              <div className="actions">
                <button type="button" className="warn-button" disabled={busy || reasonShort} onClick={() => void answer("notOk", reason)}>
                  {t("feedback.notOkSend")}
                </button>
                <button type="button" onClick={() => setNotOk(false)} disabled={busy}>
                  {t("common.cancel")}
                </button>
                {reasonShort && <span className="missing">{t("feedback.notOkMissing")}</span>}
              </div>
            </>
          )}
        </div>
      )}

      {open && (
        <div className="feedback-comment">
          <label className="field">
            <span>{t("feedback.comment")}</span>
            <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} aria-label={t("feedback.comment")} />
          </label>
          <div className="actions">
            <button type="button" disabled={busy || !comment.trim()} onClick={() => void answer("comment", comment)}>
              {t("feedback.commentSend")}
            </button>
          </div>
        </div>
      )}
      <ErrorText error={error} />
    </li>
  );
}
